import type { FragmentOf } from "@opensea/graphql"
import { graphql, readFragment } from "@opensea/graphql"

export const FindActiveDropStageFragment = graphql(`
  fragment findActiveDropStage on Drop {
    __typename
    stages {
      __typename
      stageIndex
      startTime
      endTime
      eligiblePrice {
        token {
          unit
          symbol
        }
        usd
      }
      price {
        token {
          unit
          symbol
        }
        usd
      }
      ... on Erc1155SeaDropV2Stage {
        fromTokenId
        toTokenId
      }
    }
  }
`)

/**
 * Finds the currently active stage from an array of drop stages
 * Merges stages with the same stageIndex and creates token ID mapping for ERC1155 stages
 * @param dropKey - The drop fragment containing stages
 * @returns The active stage object (always returns a stage if stages exist)
 */
export function findActiveDropStage(
  dropKey: FragmentOf<typeof FindActiveDropStageFragment>,
) {
  const drop = readFragment(FindActiveDropStageFragment, dropKey)

  if (!drop.stages.length) {
    throw new Error("No stages found in drop")
  }

  const now = Date.now()
  const isErc1155Drop = drop.__typename === "Erc1155SeaDropV2"

  // Group stages by stageIndex and merge them
  const stagesByIndex = new Map()

  for (const stage of drop.stages) {
    const stageIndex = stage.stageIndex

    if (stagesByIndex.has(stageIndex)) {
      // Merge with existing stage
      const existing = stagesByIndex.get(stageIndex)
      const existingStartTime = existing.startTime
        ? new Date(existing.startTime).getTime()
        : 0
      const existingEndTime = existing.endTime
        ? new Date(existing.endTime).getTime()
        : Number.POSITIVE_INFINITY
      const newStartTime = stage.startTime
        ? new Date(stage.startTime).getTime()
        : 0
      const newEndTime = stage.endTime
        ? new Date(stage.endTime).getTime()
        : Number.POSITIVE_INFINITY

      // Add token ID mappings only for ERC1155 drops
      if (isErc1155Drop && "fromTokenId" in stage && "toTokenId" in stage) {
        const fromTokenId = stage.fromTokenId
        const toTokenId = stage.toTokenId

        for (let tokenId = fromTokenId; tokenId <= toTokenId; tokenId++) {
          existing.tokenIdMapping.set(String(tokenId), {
            price: stage.eligiblePrice || stage.price,
          })
        }
      }

      stagesByIndex.set(stageIndex, {
        ...existing,
        startTime:
          Math.min(existingStartTime, newStartTime) === existingStartTime
            ? existing.startTime
            : stage.startTime,
        endTime:
          Math.max(existingEndTime, newEndTime) === existingEndTime
            ? existing.endTime
            : stage.endTime,
        eligiblePrice: existing.eligiblePrice || stage.eligiblePrice,
        price: existing.price || stage.price,
      })
    } else {
      const tokenIdMapping = new Map()

      // Create token ID mapping only for ERC1155 drops
      if (isErc1155Drop && "fromTokenId" in stage && "toTokenId" in stage) {
        const fromTokenId = stage.fromTokenId
        const toTokenId = stage.toTokenId

        for (let tokenId = fromTokenId; tokenId <= toTokenId; tokenId++) {
          tokenIdMapping.set(String(tokenId), {
            price: stage.eligiblePrice || stage.price,
          })
        }
      }

      stagesByIndex.set(stageIndex, {
        __typename: stage.__typename,
        stageIndex,
        startTime: stage.startTime,
        endTime: stage.endTime,
        eligiblePrice: stage.eligiblePrice,
        price: stage.price,
        ...(isErc1155Drop && { tokenIdMapping }),
      })
    }
  }

  const mergedStages = Array.from(stagesByIndex.values())

  // Find the stage that is currently active (now is between startTime and endTime)
  for (const stage of mergedStages) {
    const startTime = stage.startTime ? new Date(stage.startTime).getTime() : 0
    const endTime = stage.endTime
      ? new Date(stage.endTime).getTime()
      : Number.POSITIVE_INFINITY

    if (now >= startTime && now < endTime) {
      return stage
    }
  }

  // If no stage is currently active, return the first merged stage
  return mergedStages[0]
}
